/** 2D point for path definitions */
// ============================================================================
// DXF Configuration Types (Schema definitions)
// ============================================================================

import * as S from "effect-app/Schema"
export interface Point2D {
  x: number
  y: number
}

/** Move cursor without drawing */
export interface MoveCommand {
  type: "move"
  to: Point2D
}

/** Line segment from one point to another */
export interface LineCommand {
  type: "line"
  from: Point2D
  to: Point2D
}

/** Circular arc - preserves native arc information for DXF */
export interface ArcCommand {
  type: "arc"
  center: Point2D
  radius: number
  startAngle: number // radians, 0 = positive X axis
  endAngle: number // radians
  counterclockwise: boolean
}

/** Full circle - generates CIRCLE entity in DXF */
export interface CircleCommand {
  type: "circle"
  center: Point2D
  radius: number
}

/** Full ellipse - generated as LINE segments in DXF */
export interface EllipseCommand {
  type: "ellipse"
  center: Point2D
  radiusX: number
  radiusY: number
  rotation: number // radians
}

/** Union of all path commands */
export type PathCommand =
  | MoveCommand
  | LineCommand
  | ArcCommand
  | CircleCommand
  | EllipseCommand

/** Bounding box of a 2D shape */
export interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Metadata about the generated path */
export interface PathMetadata {
  /** Vertices for 3D extrusion (where vertical edges should be placed) */
  vertices: Point2D[]
  /** Bounding box of the 2D shape */
  bounds: Bounds
  /** Whether the path is closed */
  closed: boolean
}

/** Complete shape path with commands and metadata */
export interface ShapePath {
  commands: PathCommand[]
  metadata: PathMetadata
}

export const DXFShapeType = S.Literals([
  "rectangle",
  "roundedRectangle",
  "rectangleWith2RoundedCorners",
  "rectangleWith4IndividualCorners",
  "rectangleWith2Bevels",
  "rectangleWith4Bevels",
  "rectangleWith4IndividualBevels",
  "circle",
  "semicircle",
  "quarterCircle",
  "ellipse",
  "isoscelesTriangle",
  "rightAngleTriangle",
  "quadrangleShortEdge",
  "trapezium",
  "rhombus",
  "parallelogram",
  "star",
  "regularPolygon"
])
export type DXFShapeType = typeof DXFShapeType.Type

export const DXFColorType = S.Literals([
  "blue",
  "clear",
  "red",
  "green",
  "yellow",
  "orange",
  "pink",
  "purple",
  "black",
  "white",
  "oak",
  "walnut",
  "cherry",
  "maple",
  "mahogany",
  "pine"
])
export type DXFColorType = typeof DXFColorType.Type

export const DXFMaterialEffectType = S.Literals(["none", "glass", "glassFrosted", "wood", "mirror"])
export type DXFMaterialEffectType = typeof DXFMaterialEffectType.Type

// ============================================================================
// Geometry Types (for DXF generation and 3D rendering)
// ============================================================================

// Shared tuple types for corner styling
// Order: [TL, TR, BR, BL] (clockwise from top-left)
export const CornerRadii = S.Tuple([S.Finite, S.Finite, S.Finite, S.Finite])
export type CornerRadii = typeof CornerRadii.Type

export const CornerBevels = S.Tuple([S.Finite, S.Finite, S.Finite, S.Finite])
export type CornerBevels = typeof CornerBevels.Type

// Reusable field definitions for form schemas
export const RectangleDimensions = {
  width: S.PositiveNumber,
  height: S.PositiveNumber
}

export const CircleDimension = {
  diameter: S.PositiveNumber
}

export const QuarterCircleDimension = {
  radius: S.PositiveNumber
}

export const TriangleDimensions = {
  base: S.PositiveNumber,
  height: S.PositiveNumber
}

export const EllipseDimensions = {
  width: S.PositiveNumber,
  height: S.PositiveNumber
}

export const QuadrangleShortEdgeDimensions = {
  width: S.PositiveNumber,
  height: S.PositiveNumber,
  shortEdge: S.PositiveNumber
}

export const TrapeziumDimensions = {
  topWidth: S.PositiveNumber,
  bottomWidth: S.PositiveNumber,
  height: S.PositiveNumber
}

export const RhombusDimensions = {
  width: S.PositiveNumber,
  height: S.PositiveNumber
}

export const ParallelogramDimensions = {
  width: S.PositiveNumber,
  height: S.PositiveNumber,
  offset: S.Finite
}

export const StarDimensions = {
  outerRadius: S.PositiveNumber,
  innerRadius: S.PositiveNumber,
  points: S.PositiveInt
}

export const RegularPolygonDimensions = {
  sides: S.PositiveInt,
  radius: S.PositiveNumber
}

// Base geometry schemas (for 3D rendering)
// Rectangle variants (roundedRectangle, etc.) all use RectangleGeometry with cornerRadii/cornerBevels
export const RectangleGeometry = S.Struct({
  shape: S.Literal("rectangle"),
  width: S.Finite,
  height: S.Finite,
  depth: S.Finite,
  cornerRadii: CornerRadii,
  cornerBevels: CornerBevels
})
export type RectangleGeometry = typeof RectangleGeometry.Type

export const CircleGeometry = S.Struct({
  shape: S.Literal("circle"),
  diameter: S.Finite,
  depth: S.Finite
})
export type CircleGeometry = typeof CircleGeometry.Type

export const SemicircleGeometry = S.Struct({
  shape: S.Literal("semicircle"),
  diameter: S.Finite,
  depth: S.Finite
})
export type SemicircleGeometry = typeof SemicircleGeometry.Type

export const QuarterCircleGeometry = S.Struct({
  shape: S.Literal("quarterCircle"),
  radius: S.Finite,
  depth: S.Finite
})
export type QuarterCircleGeometry = typeof QuarterCircleGeometry.Type

export const IsoscelesTriangleGeometry = S.Struct({
  shape: S.Literal("isoscelesTriangle"),
  base: S.Finite,
  height: S.Finite,
  depth: S.Finite
})
export type IsoscelesTriangleGeometry = typeof IsoscelesTriangleGeometry.Type

export const RightAngleTriangleGeometry = S.Struct({
  shape: S.Literal("rightAngleTriangle"),
  base: S.Finite,
  height: S.Finite,
  depth: S.Finite
})
export type RightAngleTriangleGeometry = typeof RightAngleTriangleGeometry.Type

export const EllipseGeometry = S.Struct({
  shape: S.Literal("ellipse"),
  width: S.Finite,
  height: S.Finite,
  depth: S.Finite
})
export type EllipseGeometry = typeof EllipseGeometry.Type

export const QuadrangleShortEdgeGeometry = S.Struct({
  shape: S.Literal("quadrangleShortEdge"),
  width: S.Finite,
  height: S.Finite,
  shortEdge: S.Finite,
  depth: S.Finite
})
export type QuadrangleShortEdgeGeometry = typeof QuadrangleShortEdgeGeometry.Type

export const TrapeziumGeometry = S.Struct({
  shape: S.Literal("trapezium"),
  topWidth: S.Finite,
  bottomWidth: S.Finite,
  height: S.Finite,
  depth: S.Finite
})
export type TrapeziumGeometry = typeof TrapeziumGeometry.Type

export const RhombusGeometry = S.Struct({
  shape: S.Literal("rhombus"),
  width: S.Finite,
  height: S.Finite,
  depth: S.Finite
})
export type RhombusGeometry = typeof RhombusGeometry.Type

export const ParallelogramGeometry = S.Struct({
  shape: S.Literal("parallelogram"),
  width: S.Finite,
  height: S.Finite,
  offset: S.Finite,
  depth: S.Finite
})
export type ParallelogramGeometry = typeof ParallelogramGeometry.Type

export const StarGeometry = S.Struct({
  shape: S.Literal("star"),
  outerRadius: S.Finite,
  innerRadius: S.Finite,
  points: S.Finite,
  depth: S.Finite
})
export type StarGeometry = typeof StarGeometry.Type

export const RegularPolygonGeometry = S.Struct({
  shape: S.Literal("regularPolygon"),
  sides: S.Finite,
  radius: S.Finite,
  depth: S.Finite
})
export type RegularPolygonGeometry = typeof RegularPolygonGeometry.Type

// Union of all geometry types (used by DXF viewer)
export const GeometryParams = S.Union([
  RectangleGeometry,
  CircleGeometry,
  SemicircleGeometry,
  QuarterCircleGeometry,
  IsoscelesTriangleGeometry,
  RightAngleTriangleGeometry,
  EllipseGeometry,
  QuadrangleShortEdgeGeometry,
  TrapeziumGeometry,
  RhombusGeometry,
  ParallelogramGeometry,
  StarGeometry,
  RegularPolygonGeometry
])
export type GeometryParams = typeof GeometryParams.Type
